<template>
  <div class="min-h-screen bg-gray-50">
    <div class="container mx-auto px-4 py-8 max-w-3xl">
      <div class="flex items-center space-x-4 mb-6">
        <button @click="goBack" class="p-2 hover:bg-gray-100 rounded-lg">
          <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </button>
        <h1 class="text-2xl font-bold text-gray-900">KYC Verification</h1>
      </div>

      <!-- Documents -->
      <div
        v-for="doc in documents"
        :key="doc.ctype"
        class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4"
      >
        <div class="flex items-center gap-4">
          <div
            @click="openImageModal(doc.data)"
            class="shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors group"
          >
            <svg
              class="w-7 h-7 text-gray-500 group-hover:text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <p class="text-sm font-medium text-gray-900">{{ doc.name }}</p>
              <StatusBadge :status="getDocumentDecision(doc.ctype)?.decision_label ?? 'Pending'" />
            </div>
            <p v-if="getDocumentDecision(doc.ctype)?.summary" class="text-sm text-gray-600 mt-1">
              {{ getDocumentDecision(doc.ctype).summary }}
            </p>
            <button
              @click="openImageModal(doc.data)"
              class="text-xs font-medium text-blue-600 hover:text-blue-800 mt-1"
            >
              Click to view document
            </button>
          </div>
        </div>

        <div class="mt-4 pt-4 border-t border-gray-100">
          <p v-if="isCtypeVerified(doc.ctype)" class="text-sm text-gray-500 italic">
            Already verified — decision cannot be changed here.
          </p>
          <button
            v-else
            @click="openDecisionModal(doc)"
            class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700 text-sm"
          >
            Submit Decision
          </button>
        </div>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <div v-if="showImageModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" @click.self="closeImageModal">
      <div class="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        <div class="flex justify-between items-center p-4 border-b">
          <h3 class="text-xl font-semibold text-gray-800">Document Preview</h3>
          <button @click="closeImageModal" class="text-gray-500 hover:text-gray-700">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="p-6 overflow-auto max-h-[calc(90vh-120px)]">
          <img :src="previewImage" alt="Document preview" class="max-w-full h-auto rounded-lg shadow-md mx-auto" />
        </div>
        <div class="flex justify-end p-4 border-t bg-gray-50">
          <button @click="closeImageModal" class="px-6 py-2 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    </div>

    <!-- Decision Modal -->
    <div v-if="showDecisionModal" class="fixed inset-0 z-50 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75" @click="closeDecisionModal"></div>
        <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-6 pt-5 pb-4 sm:p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-4">
              {{ selectedDocument?.name }} — Decision
            </h3>
            <form @submit.prevent="submit">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Decision</label>
                <select v-model="form.decision" required class="w-full border border-gray-300 rounded-lg px-3 py-2">
                  <option value="">Select decision</option>
                  <option :value="1">Approve</option>
                  <option :value="0">Reject</option>
                </select>
              </div>
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-700 mb-2">Summary</label>
                <textarea v-model="form.summary" required rows="4" class="w-full border border-gray-300 rounded-lg px-3 py-2"></textarea>
              </div>
              <div class="mt-6 flex justify-end space-x-3">
                <button type="button" @click="closeDecisionModal" class="px-4 py-2 border border-red-400 text-red-400 rounded-lg">Cancel</button>
                <button type="submit" :disabled="isSubmitting" class="px-4 py-2 bg-indigo-800 text-white rounded-lg hover:bg-indigo-700">
                  {{ isSubmitting ? "Submitting..." : "Submit" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import StatusBadge from "@/components/ui/StatusBadge.vue";
import api from "@/utils/api";
import { useNotifications } from "@/composables/useNotifications";

const route = useRoute();
const router = useRouter();
const { showError, formatValidationErrors } = useNotifications();

const documents = ref([]);
const verifications = ref([]);

const showImageModal = ref(false);
const previewImage = ref("");

const showDecisionModal = ref(false);
const isSubmitting = ref(false);
const selectedDocument = ref(null);
const form = ref({ ctype: "", decision: "", summary: "" });

const goBack = () => router.push(`/admin/tickets/${route.params.id}`);

// Resolve a stored file path/URL/base64 string into something an <img> can load
const resolveDocumentSrc = (data) => {
  if (!data) return "";
  if (data.startsWith("data:image")) return data;
  if (data.startsWith("http://") || data.startsWith("https://")) return data;
  const base = (api.defaults.baseURL || "").replace(/\/$/, "");
  return `${base}${data.startsWith("/") ? "" : "/"}${data}`;
};

const openImageModal = (data) => {
  previewImage.value = resolveDocumentSrc(data);
  showImageModal.value = true;
  document.body.style.overflow = "hidden";
};

const closeImageModal = () => {
  showImageModal.value = false;
  document.body.style.overflow = "auto";
};

const getDocumentDecision = (ctype) => {
  const records = verifications.value.filter((v) => v.ctype === ctype);
  if (!records.length) return null;
  return records.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
};

const isCtypeVerified = (ctype) => getDocumentDecision(ctype) !== null;

const fetchVerifications = async () => {
  try {
    const response = await api.get("/api/v2/kyc-verifications", {
      params: { ticket_id: route.params.id },
    });
    verifications.value = response.data;
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load KYC verifications", details, 5000);
  }
};

const fetchDocuments = async () => {
  try {
    const response = await api.get(`/api/v1/tickets/${route.params.id}`);
    const clientDoc = response.data?.client?.client_document;
    // ctype values match App\Models\KycVerification constants:
    // 1 = ID Front, 2 = ID Back, 3 = Selfie
    documents.value = [
      { ctype: 1, name: "ID Front", data: clientDoc?.id_front },
      { ctype: 2, name: "ID Back", data: clientDoc?.id_back },
      { ctype: 3, name: "Selfie", data: clientDoc?.selfie },
    ];
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to load documents", details, 5000);
  }
};

const openDecisionModal = (doc) => {
  selectedDocument.value = doc;
  form.value = { ctype: doc.ctype, decision: "", summary: "" };
  showDecisionModal.value = true;
};

const closeDecisionModal = () => {
  showDecisionModal.value = false;
};

const submit = async () => {
  isSubmitting.value = true;
  try {
    await api.post("/api/v2/kyc-verifications", {
      ...form.value,
      ticket_id: route.params.id,
      client_id: route.query.client_id,
    });
    closeDecisionModal();
    fetchVerifications();
  } catch (error) {
    const details = formatValidationErrors(error);
    showError("Error", "Failed to submit KYC verification", details, 5000);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  fetchVerifications();
  fetchDocuments();
});
</script>